import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Container, Stack, Typography, alpha } from '@mui/material';

const INK = '#0F172A';
const MUTED = '#64748B';
const SOFT = '#F8FAFC';
const SURFACE = '#FFFFFF';
const BORDER = '#E2E8F0';
const ACCENT = '#7C3AED';

export default function PublicHeader({ onBookDemo }) {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(() => window.scrollY > 20);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const primaryButtonSx = {
    background: `linear-gradient(135deg, ${ACCENT} 0%, #6366F1 100%)`,
    color: "#FFFFFF",
    borderRadius: "12px",
    px: 3.5,
    py: 1.4,
    fontWeight: 800,
    fontSize: "15px",
    textTransform: "none",
    boxShadow: "0 8px 24px rgba(124, 58, 237, 0.35)",
    transition: "all 0.25s ease",
    "&:hover": {
      background: `linear-gradient(135deg, #8B5CF6 0%, #4F46E5 100%)`,
      boxShadow: "0 12px 30px rgba(124, 58, 237, 0.5)",
      transform: "translateY(-2px)",
    },
  };

  return (
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: scrolled ? alpha(SURFACE, 0.94) : alpha(SURFACE, 0.85),
          backdropFilter: "blur(20px)",
          borderBottom: `1px solid ${scrolled ? BORDER : "transparent"}`,
          transition: "all 0.3s ease",
          py: 1.5,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 0 }} alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5} component={RouterLink} to="/" sx={{ textDecoration: "none" }}>
              <Box
                component="img"
                src="/logo.png"
                alt="Fitistic"
                sx={{ width: 38, height: 38, objectFit: "contain", borderRadius: 2 }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <Typography sx={{ fontWeight: 900, fontSize: 22, color: INK, letterSpacing: "-0.03em" }}>
                Fitistic<Box component="span" sx={{ color: ACCENT }}>.</Box>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={3.5} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
              <Typography component="a" href={pathname === "/" ? "#how-it-works" : "/#how-it-works"} sx={{ color: MUTED, textDecoration: "none", fontWeight: 700, fontSize: 14, "&:hover": { color: ACCENT } }}>
                How it works
              </Typography>
              <Typography component="a" href={pathname === "/" ? "#features" : "/#features"} sx={{ color: MUTED, textDecoration: "none", fontWeight: 700, fontSize: 14, "&:hover": { color: ACCENT } }}>
                Features
              </Typography>
              <Typography component="a" href={pathname === "/" ? "#roi-calculator" : "/#roi-calculator"} sx={{ color: MUTED, textDecoration: "none", fontWeight: 700, fontSize: 14, "&:hover": { color: ACCENT } }}>
                ROI Calculator
              </Typography>
              <Typography component="a" href={pathname === "/" ? "#testimonials" : "/#testimonials"} sx={{ color: MUTED, textDecoration: "none", fontWeight: 700, fontSize: 14, "&:hover": { color: ACCENT } }}>
                Reviews
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button component={RouterLink} to="/pricing" sx={{ color: ACCENT, fontWeight: 800 }}>Pricing</Button>
              <Button
                variant="text"
                component={RouterLink} to="/login"
                sx={{ color: INK, fontWeight: 800, px: 2.5, "&:hover": { bgcolor: SOFT } }}
              >
                Admin Login
              </Button>
              <Button
                variant="contained"
                component={onBookDemo ? 'button' : RouterLink}
                to={onBookDemo ? undefined : '/?demo=1'}
                onClick={onBookDemo}
                sx={{ ...primaryButtonSx, px: 3, py: 1 }}
              >
                Book Demo
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
  );
}
